import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion"
import { ScrollArea, Collapsible, Select } from "radix-ui"
import { AnimatePresence, motion } from "motion/react"
import { ArrowCircleLeft, ArrowCircleRight, Remove, PlayArrow, Add, Info, Check, ExpandMore, Pause, QuestionAnswer, UnfoldLess, UnfoldMore, ArrowUpward, ArrowDownward } from "@mui/icons-material"
import { cn } from "src/utils/Functions"
import { toProperCase } from "src/utils/Strings"
import { getContractViewerContent } from "@/components/contract-viewer/utils"
import { AudioButton, useAudio } from "src/hooks/useAudio"
import { parseAllVariableTags, type ContractVariableMap, type GetContractResponseContent, type ContractVariable, type IqModeSection } from "src/types/contract.types"

type Perspective = string

// Define ordered section keys for consistent accordion ordering
const SECTION_ORDER = [
  "earnings",
  "qualityOfRights", 
  "usageObligations",
  "agreementLength",
  "liabilitySafeguards"
] as const

// Type-safe section entry with preserved key-value relationship
type SectionEntry = {
  key: string
  section: IqModeSection
}

// Helper function to get sections in defined order
const getOrderedSections = (sections: Record<string, IqModeSection> | undefined): SectionEntry[] => {
  if (!sections) return []
  
  const availableKeys = Object.keys(sections)
  const orderedEntries: SectionEntry[] = []
  
  // Add sections in defined order
  SECTION_ORDER.forEach(key => {
    if (availableKeys.includes(key) && sections[key]) {
      orderedEntries.push({
        key,
        section: sections[key]
      })
    }
  })
  
  // Add any remaining sections not in the defined order
  availableKeys.forEach(key => {
    if (!(SECTION_ORDER as readonly string[]).includes(key) && sections[key]) {
      orderedEntries.push({
        key,
        section: sections[key]
      })
    }
  })
  
  return orderedEntries
}

// Interface for term data structure used in Citation component
interface CitationTermData extends ContractVariable {
  title: string
  definition: string
  citation: string
}

// Utility function to filter out numbered duplicates from ContractVariableMap
const filterNumberedDuplicates = (variables: ContractVariableMap): ContractVariableMap => {
  const filtered: ContractVariableMap = {}
  
  Object.entries(variables).forEach(([id, variable]) => {
    // Check if this is a numbered duplicate (e.g., "performance_1")
    if (/.*_\d+$/.test(id)) {
      const baseId = id.replace(/_\d+$/, '')
      // Only include numbered version if base version doesn't exist
      if (!variables[baseId]) {
        filtered[id] = variable
      }
    } else {
      // Always include non-numbered variables
      filtered[id] = variable
    }
  })
  
  return filtered
}
const highlightTermsInText = (text: string, variables: ContractVariableMap, onTermClick: (termId: string) => void) => {
  if (!text || !Object.keys(variables).length) return text;

  // Check if text is already processed (contains term markers)
  if (text.includes('__TERM_')) {
    // If already processed, just parse the existing markers
    const parts = text.split(/(__TERM_.+?__)/);
    return parts.map((part, index) => {
      const termMatch = part.match(/^__TERM_(.+?)__$/);
      if (termMatch) {
        const termId = termMatch[1];
        const term = variables[termId];
        if (term && term.name) {
          return (
            <span
              key={index}
              className="text-blue-400 underline cursor-pointer hover:text-blue-300 transition-colors"
              data-term-id={termId}
              onClick={() => onTermClick(termId)}
            >
              {term.name}
            </span>
          );
        }
      }
      return part;
    });
  }
  
  let processedText = text;

  // Sort variables with valid names by length (longest first) to avoid partial matches
  const sortedVariables = Object.values(variables)
    .filter((v): v is ContractVariable & { name: string } =>
      typeof v?.name === 'string' && v.name.trim().length >= 2
    )
    .sort((a, b) => b.name.length - a.name.length);
  sortedVariables.forEach((variable) => {

    // Escape special regex characters
    const escapedVariable = variable.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Use word boundary regex for better matching
    const variableRegex = new RegExp(`\\b${escapedVariable}\\b`, 'gi');

    // Only replace if the variable hasn't already been processed
    if (
      processedText.match(variableRegex) &&
      !processedText.includes(`__TERM_${variable.id}__`)
    ) {
      processedText = processedText.replace(
        variableRegex,
        `__TERM_${variable.id}__`
      );
    }
  });
  
  // Split by term markers and reconstruct with React elements
  const parts = processedText.split(/(__TERM_.+?__)/);
  
  return parts.map((part, index) => {
    const termMatch = part.match(/^__TERM_(.+?)__$/);
    if (termMatch) {
      const termId = termMatch[1];
      const term = variables[termId];
      if (term) {
        return (
          <span
            key={index}
            className="text-blue-400 underline cursor-pointer hover:text-blue-300 transition-colors"
            data-term-id={termId}
            onClick={() => onTermClick(termId)}
          >
            {term.name}
          </span>
        );
      } else {
        console.warn(`Term not found for ID: ${termId}`);
        return null;
      }
    }
    return part;
  });
};

interface IqModeAnalysisProps {
  contract: GetContractResponseContent
  questionsOnly?: boolean
  externalHighlightedTerm?: string | null
  onTermSelect?: (termWithInstance: string) => void
  viewerOnly?: boolean
}

export default function IqModeAnalysis({
  contract,
  questionsOnly = false,
  externalHighlightedTerm = null,
  onTermSelect,
  viewerOnly = false,
}: IqModeAnalysisProps) {
  if (questionsOnly) {
    return <IqModeFaq contract={contract} onTermSelect={onTermSelect} />
  }

  return (
    <IqModeViewer
      contract={contract}
      externalHighlightedTerm={externalHighlightedTerm}
      viewerOnly={viewerOnly}
    />
  )
}

type IqModeViewerProps = Pick<IqModeAnalysisProps, "contract" | "externalHighlightedTerm" | "viewerOnly">

function IqModeViewer({ contract, externalHighlightedTerm = null, viewerOnly = false }: IqModeViewerProps) {
  const [highlightedTerm, setHighlightedTerm] = useState<string | null>(null)
  const shouldScrollToTerm = useRef(false)

  useEffect(() => {
    if (!externalHighlightedTerm) return
    shouldScrollToTerm.current = true
    setHighlightedTerm(externalHighlightedTerm)
  }, [externalHighlightedTerm])

  return (
    <div className={cn("text-white h-full", viewerOnly && "w-full")}>
      <ContractViewer
        highlightedTerm={highlightedTerm}
        contract={contract}
        setHighlightedTerm={setHighlightedTerm}
        shouldScrollToTerm={shouldScrollToTerm}
        fullHeight
      />
    </div>
  )
}

type IqModeFaqProps = Pick<IqModeAnalysisProps, "contract" | "onTermSelect">

function IqModeFaq({ contract, onTermSelect }: IqModeFaqProps) {
  const availablePerspectives = useMemo(
    () => Object.keys(contract?.iqData?.iqModeData || {}),
    [contract?.iqData?.iqModeData]
  )
  const [selectedPerspective, setSelectedPerspective] = useState<Perspective>(availablePerspectives[0] || "")
  const [openAccordions, setOpenAccordions] = useState<string[]>([])
  const { currentAudio, isPlaying } = useAudio()

  useEffect(() => {
    if (availablePerspectives.length > 0 && !availablePerspectives.includes(selectedPerspective)) {
      setSelectedPerspective(availablePerspectives[0])
    }
  }, [availablePerspectives, selectedPerspective])

  const allVariables = useMemo(() => {
    const variables = contract?.contractExtraction?.variables || {}
    return filterNumberedDuplicates(variables)
  }, [contract?.contractExtraction?.variables])

  const orderedSections = useMemo(() => {
    return getOrderedSections(
      contract?.iqData?.iqModeData?.[selectedPerspective]?.sections
    )
  }, [contract?.iqData?.iqModeData, selectedPerspective])

  const handleAccordionTermClick = useCallback(
    (termId: string) => {
      const termWithInstance = termId.includes(":") ? termId : `${termId}:1`
      onTermSelect?.(termWithInstance)
    },
    [onTermSelect]
  )

  return (
    <div className="text-white [&>p]:m-0!important">
      <PerspectiveModeSelector
        perspective={selectedPerspective}
        setPerspective={setSelectedPerspective}
        availablePerspectives={availablePerspectives}
        contract={contract}
      />
      <div className="mt-4 md:sticky md:top-4 transition-all">
        <AccordionPrimitive.Root multiple value={openAccordions} onValueChange={(value) => setOpenAccordions(value as string[])} className="space-y-2">
          {orderedSections.map(({ section }) => {
            return (
            <SectionAccordionItem
              key={section.id}
              icon={toProperCase(section.sectionTitle).charAt(0)}
              title={toProperCase(section.sectionTitle)}
              value={section.id}
          >
              <AccordionPrimitive.Root className="">
                {section.questions?.map((question, index: number) => {
                  return (
                  <AccordionPrimitive.Item
                    className="border-b last:border-b-0 border-neutral-700 overflow-hidden"
                    value={index.toString()}
                    key={index}
                  >
                    <AccordionPrimitive.Header className="flex">
                      <AccordionPrimitive.Trigger className="group flex w-full items-start gap-2 py-3 text-left text-sm font-medium text-white/80 transition-colors outline-none hover:text-white focus-visible:ring-[3px] focus-visible:ring-ring [&[data-panel-open]_.question-chevron]:rotate-180">
                        <div className="w-9 h-9 flex items-center text-white justify-center bg-gradient-to-r from-[#547bb5] to-[#6e53b8] rounded-full">
                          <QuestionAnswer fontSize="small" />
                        </div>
                        <div className="flex-1 pt-1 leading-6">
                          {highlightTermsInText(
                            typeof question.question === 'string' ? question.question : question.question?.text || '',
                            allVariables,
                            handleAccordionTermClick
                          )}
                        </div>
                        <ExpandMore className="question-chevron mt-1 size-4 text-white/60 transition-transform duration-200 ease-in-out" />
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Panel className="h-(--accordion-panel-height) overflow-hidden text-sm text-white/70 transition-[height,opacity] duration-200 ease-in-out data-ending-style:h-0 data-starting-style:h-0 data-ending-style:opacity-0 data-starting-style:opacity-0">
                      <div className="pb-3 pl-11 pr-1">
                        <div className="leading-6">
                          {highlightTermsInText(
                            typeof question.answer === 'string' ? question.answer : question.answer?.text || '',
                            allVariables,
                            handleAccordionTermClick
                          )}
                        </div>
                        <AudioButton
                          audioSrc={question.ttsSrcUrl || ""}
                          className={cn("flex items-center gap-1 px-4 py-2 bg-white/10 rounded-full mt-4")}
                        >
                          {(() => {
                            const audioSrc = question.ttsSrcUrl || ""
                            return currentAudio === audioSrc && isPlaying ? (
                              <Pause fill="currentColor" className="w-4 h-4" />
                            ) : (
                              <PlayArrow fill="currentColor" className="w-4 h-4" />
                            )
                          })()}
                          Listen to what this means for you
                        </AudioButton>
                      </div>
                    </AccordionPrimitive.Panel>
                  </AccordionPrimitive.Item>
                  )
                })}
              </AccordionPrimitive.Root>
          </SectionAccordionItem>
            )
          })}
        </AccordionPrimitive.Root>
      </div>
    </div>
  )
}

type SectionAccordionItemProps = React.ComponentProps<typeof AccordionPrimitive.Item> & {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

const SectionAccordionItem = (props: SectionAccordionItemProps) => {
  return (
    <AccordionPrimitive.Item {...props} className="bg-neutral-800 p-3 rounded-[2rem] overflow-hidden">
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger className="group flex items-center justify-between w-full rounded-md py-1 text-left text-base font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring [&[data-panel-open]_.section-chevron]:rotate-180">
          <div className="flex items-center gap-2">
          <p className="text-neutral-400 bg-white/10 m-0 rounded-full size-10 flex items-center justify-center">{props.icon}</p>
          <h1 className="text-base m-0">{props.title}</h1>
          </div>
          <ExpandMore className="section-chevron w-4 h-4 mr-2 text-white/70 transition-transform duration-200 ease-in-out" />
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height,opacity] duration-200 ease-in-out data-ending-style:h-0 data-starting-style:h-0 data-ending-style:opacity-0 data-starting-style:opacity-0">
        <div className="px-2 pt-3 pb-2">
          {props.children}
        </div>
      </AccordionPrimitive.Panel>
    </AccordionPrimitive.Item>
  )
}

type PerspectiveModeProps = {
  perspective: Perspective
  setPerspective: (perspective: Perspective) => void
  availablePerspectives: string[]
  contract: GetContractResponseContent
}

const PerspectiveModeSelector = ({ perspective, setPerspective, availablePerspectives, contract }: PerspectiveModeProps) => {
  // Get party names from contract or use generic fallbacks
  const getPartyName = (perspectiveKey: string, index: number) => {
    const parties = contract?.contractExtraction?.parties || []
    
    // First try exact match
    const exactMatch = parties.find(party => party === perspectiveKey)
    if (exactMatch) return exactMatch
    
    const partialMatch = parties.find(party => perspectiveKey.includes(party))
    if (partialMatch) return perspectiveKey
    
    // Fall back to index-based matching
    return parties[index] || `Party ${index + 1}`
  }

  return (
    <div>
      <Select.Root value={perspective} onValueChange={(value) => {
        setPerspective(value as Perspective)
      }}>
        <Select.Trigger className="px-2 py-2 rounded-full border border-white/10 w-max flex items-center gap-4 cursor-pointer focus:outline-none">
          <Info className="w-4 h-4" />
          <p className="text-white/60 m-0">{availablePerspectives.length} Perspectives</p>
          <UserIcon className="w-6 h-6" />
          <Select.Value placeholder="Select a perspective" />
          <ExpandMore className="size-6" />
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="bg-neutral-800/90 backdrop-blur-sm border border-white/10 rounded-xl p-1 w-[var(--radix-select-trigger-width)] shadow-lg data-[state=open]:animate-open-select data-[state=closed]:animate-close-select" side="bottom" sideOffset={4} position="popper" align="start">
            <Select.Viewport>
              <Select.Group>  
                {availablePerspectives.map((perspectiveKey, index) => (
                  <Select.Item 
                    key={perspectiveKey} 
                    value={perspectiveKey} 
                    className="px-3 py-2 text-white rounded-lg cursor-pointer hover:bg-white/5 data-[state=checked]:bg-white/10 focus:outline-none"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                        {perspective === perspectiveKey && <Check className="w-4 h-4" />}
                      </div>
                      <Select.ItemText className="text-white/60 flex-1">
                        {getPartyName(perspectiveKey, index)}
                      </Select.ItemText>
                    </div>
                  </Select.Item>
                ))}
              </Select.Group>
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}


const ContractViewer = ({
  highlightedTerm,
  contract,
  setHighlightedTerm,
  shouldScrollToTerm,
  fullHeight = false,
}: {
  highlightedTerm: string | null
  contract: GetContractResponseContent
  setHighlightedTerm: (term: string | null) => void
  shouldScrollToTerm: React.MutableRefObject<boolean>
  fullHeight?: boolean
}) => {
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isCitationOpen, setIsCitationOpen] = useState(false)

  useEffect(() => {
    if (highlightedTerm && scrollViewportRef.current && shouldScrollToTerm.current) {
      let termElement: Element | null = null
      if (highlightedTerm.includes(':')) {
        const [termId, instance] = highlightedTerm.split(':')
        termElement = scrollViewportRef.current.querySelector(`[data-term-id="${termId}"][data-instance="${instance}"]`)
      } else {
        termElement = scrollViewportRef.current.querySelector(`[data-term-id="${highlightedTerm}"]`)
      }

      if (termElement && termElement instanceof HTMLElement) {
        const viewportRect = scrollViewportRef.current.getBoundingClientRect()
        const termRect = termElement.getBoundingClientRect()
        const relativeTop = termRect.top - viewportRect.top
        const scrollOffset = scrollViewportRef.current.scrollTop + relativeTop - (scrollViewportRef.current.clientHeight / 2) + (termRect.height / 2)
        scrollViewportRef.current.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        })
      }
      shouldScrollToTerm.current = false
    }
  }, [highlightedTerm, shouldScrollToTerm])

  return (
    <div
      className={cn(
        "bg-neutral-800 p-6 pb-[calc(84px+1rem)] md:pb-6 rounded-t-[2rem] md:rounded-[1.75rem] flex flex-col overflow-hidden",
        fullHeight ? "h-full w-full" : "md:sticky md:bottom-0 self-start",
        fullHeight ? null : isExpanded ? "h-[100svh]" : "h-[90svh] md:h-[600px]"
      )}
    >
      <div className="flex items-center justify-between flex-shrink-0">
        <h1 className="text-xl font-medium">Contract Viewer</h1>
        {!fullHeight && (
          <button 
            onClick={() => {
              setIsExpanded(!isExpanded)
              if (!isExpanded) {
                setIsCitationOpen(false)
              }
            }}
            className="mr-14 md:mr-0 md:hidden flex items-center gap-2 px-4 py-2 pr-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            {isExpanded ? (
              <>
                <span className="">Collapse</span>
                <UnfoldLess className="w-4 h-4 text-white/60 rotate-45" />
              </>
            ) : (
              <>
                <span className="">Expand</span>
                <UnfoldMore className="w-4 h-4 text-white/60 rotate-45" />
              </>
            )}
          </button>
        )}
      </div>

      <Divider />

    <ScrollArea.Root className={cn(
      "[mask-image:linear-gradient(to_bottom,black_90%,transparent_100%)]",
      "flex-1 min-h-0"
    )} type="always">
      <ScrollArea.Viewport ref={scrollViewportRef} 
      className="py-4 w-[95%] h-full">
        {getContractViewerContent(highlightedTerm, contract, (termWithInstance) => {
          // Extract base term ID from "termId:instance" format
          const baseTermId = termWithInstance.includes(':') 
            ? termWithInstance.split(':')[0] 
            : termWithInstance
          
          if (contract.contractExtraction?.variables) {
            const variable = contract.contractExtraction.variables[baseTermId]
            if (variable) {
              // Store the term with instance info to scroll to the exact instance
              setHighlightedTerm(termWithInstance)
            }
          }
        })}
      </ScrollArea.Viewport>

      <ScrollArea.Scrollbar
        className="flex rounded-full touch-none select-none bg-neutral-700 p-0.5 transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
        orientation="vertical"
      >
        <ScrollArea.Thumb className="relative flex-1 rounded-full bg-gradient-to-b from-[#547BB5] to-[#6E53B8] before:absolute before:left-1/2 before:top-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>

    <Citation highlightedTerm={highlightedTerm} contract={contract} isOpen={isCitationOpen} setIsOpen={setIsCitationOpen} setIsExpanded={setIsExpanded} shouldScrollToTerm={shouldScrollToTerm} setHighlightedTerm={setHighlightedTerm} />
  </div>
  )
}

const Divider = () => {
  return (
    <div className="h-[1px] bg-white/10 my-4" />
  )
}

const Citation = ({ highlightedTerm, contract, isOpen, setIsOpen, setIsExpanded, shouldScrollToTerm, setHighlightedTerm }: { highlightedTerm: string | null, contract: GetContractResponseContent, isOpen: boolean, setIsOpen: (open: boolean) => void, setIsExpanded: (expanded: boolean) => void, shouldScrollToTerm: React.MutableRefObject<boolean>, setHighlightedTerm: (term: string | null) => void }) => {
  const hasAutoNavigatedRef = useRef<string | null>(null)

  const allTerms = useMemo(() => {
    const terms: CitationTermData[] = []

    if (contract?.contractExtraction?.variables) {
      const variables = contract.contractExtraction.variables;
      const filteredVariables = filterNumberedDuplicates(variables)

      Object.values(filteredVariables).forEach((variable: ContractVariable) => {
        terms.push({
          ...variable,
          title: variable.name,
          definition: variable.value || 'No definition available',
          citation: variable.definitionCitation || variable.referencedSection || variable.context || `${variable.type} term`
        })
      })
    }

    return terms
  }, [contract])

  // Parse contract XML once and cache instance counts for all terms
  const termInstanceCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    const contractText = contract?.contractExtraction?.contractTexts?.taggedText?.text || ''

    if (!contractText) return counts

    // Parse once and build a map of term ID to instance count
    const tags = parseAllVariableTags(contractText, { preserveInstance: true })
    tags.forEach(tag => {
      const termId = tag.attributes.id
      counts[termId] = (counts[termId] || 0) + 1
    })

    return counts
  }, [contract])

  const [currentItem, setCurrentItem] = useState(0)
  const [currentInstance, setCurrentInstance] = useState(1)
  const [direction, setDirection] = useState<1 | -1>(1) // 1 for next, -1 for prev

  // Get instance count for current term from cached map
  const instanceCount = useMemo(() => {
    if (!allTerms[currentItem]) return 1
    const termId = allTerms[currentItem].id
    return termInstanceCounts[termId] || 1
  }, [currentItem, allTerms, termInstanceCounts])

  // If a variable is highlighted and we haven't auto-navigated to it yet, open citation and navigate to that variable
  useEffect(() => {
    if (highlightedTerm && highlightedTerm !== hasAutoNavigatedRef.current) {
      setIsOpen(true)
      setIsExpanded(false)

      // Extract base term ID and instance from "termId:instance" format
      const baseTermId = highlightedTerm.includes(':')
        ? highlightedTerm.split(':')[0]
        : highlightedTerm
      const instance = highlightedTerm.includes(':')
        ? parseInt(highlightedTerm.split(':')[1]) || 1
        : 1

      const termIndex = allTerms.findIndex(term => term.id === baseTermId)
      if (termIndex !== -1) {
        setCurrentItem(termIndex)
        setCurrentInstance(instance)
        hasAutoNavigatedRef.current = highlightedTerm
      }
    }
  }, [highlightedTerm, allTerms, setIsExpanded, setIsOpen])

  const slideVariants = {
    enter: (direction: number) => ({
      y: direction > 0 ? "32px" : "-32px",
      opacity: 0
    }),
    center: {
      y: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      y: direction > 0 ? "-32px" : "32px",
      opacity: 0
    })
  }

  // Horizontal navigation (different terms) - resets to instance 1
  const handleNext = () => {
    setDirection(1)
    const nextIndex = Math.min(currentItem + 1, allTerms.length - 1)
    setCurrentItem(nextIndex)
    setCurrentInstance(1)
    shouldScrollToTerm.current = true
    // Update highlighted term to trigger scroll
    if (allTerms[nextIndex]) {
      setHighlightedTerm(`${allTerms[nextIndex].id}:1`)
    }
  }

  const handlePrevious = () => {
    setDirection(-1)
    const prevIndex = Math.max(currentItem - 1, 0)
    setCurrentItem(prevIndex)
    setCurrentInstance(1)
    shouldScrollToTerm.current = true
    // Update highlighted term to trigger scroll
    if (allTerms[prevIndex]) {
      setHighlightedTerm(`${allTerms[prevIndex].id}:1`)
    }
  }

  // Vertical navigation (different instances of same term)
  const handleNextInstance = () => {
    const nextInstance = Math.min(currentInstance + 1, instanceCount)
    setCurrentInstance(nextInstance)
    shouldScrollToTerm.current = true
    if (allTerms[currentItem]) {
      setHighlightedTerm(`${allTerms[currentItem].id}:${nextInstance}`)
    }
  }

  const handlePreviousInstance = () => {
    const prevInstance = Math.max(currentInstance - 1, 1)
    setCurrentInstance(prevInstance)
    shouldScrollToTerm.current = true
    if (allTerms[currentItem]) {
      setHighlightedTerm(`${allTerms[currentItem].id}:${prevInstance}`)
    }
  }

  const currentTerm = allTerms[currentItem]

  return (
    <div className="p-4 bg-neutral-700 border border-white/5 shadow-md rounded-2xl mt-4 overflow-hidden">
      <Collapsible.Root className="" open={isOpen} onOpenChange={(open) => {
        setIsOpen(open)
        if (open) {
          setIsExpanded(false)
        }
      }}>
          <Collapsible.Trigger className="flex items-center justify-between w-full">
            <h2 className="text-lg">
              Citation
            </h2>

            <Divider />

            <div className="flex items-center gap-2">
              {isOpen ? <Remove className="w-4 h-4" /> : <Add className="w-4 h-4" />}
            </div>
          </Collapsible.Trigger>
          <Collapsible.Content className="data-[state=open]:animate-slide-down-content data-[state=closed]:animate-slide-up-content overflow-hidden">

            {/* Vertical navigation for instances */}
            {instanceCount > 1 && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <button className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors" onClick={handlePreviousInstance} disabled={currentInstance === 1}>
                    <ArrowUpward className="size-6" />
                  </button>
                  <button className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors" onClick={handleNextInstance} disabled={currentInstance === instanceCount}>
                    <ArrowDownward className="size-6" />
                  </button>
                </div>
                <div className="text-white/60 text-sm">
                  Reference {currentInstance} / {instanceCount}
                </div>
              </div>
            )}

            <AnimatePresence mode="popLayout" initial={false} custom={direction}>
              <motion.div
                key={currentItem}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                {currentTerm && <CitationItem termData={currentTerm} />}
              </motion.div>
            </AnimatePresence>

            {/* Horizontal navigation for terms */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <button className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors" onClick={handlePrevious} disabled={currentItem === 0}>
                  <ArrowCircleLeft className="size-8" />
                </button>
                <button className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors" onClick={handleNext} disabled={currentItem === allTerms.length - 1}>
                  <ArrowCircleRight className="size-8" />
                </button>
              </div>
              <div className="text-white/60">
                Term {currentItem + 1} / {allTerms.length}
              </div>
            </div>
          </Collapsible.Content>
      </Collapsible.Root>
    </div>
  )
}

const CitationItem = ({ termData }: { termData: CitationTermData }) => {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-white/60">{termData.citation}</p>

      <div>
        <h4 className="text-lg mb-4">{termData.title}</h4>

        <p className="text-white/60 mb-2">Definition</p>
        <div className="overflow-y-auto [mask-image:linear-gradient(to_bottom,black_90%,transparent_100%)]">
          <p className="text-white/60 mb-4">{termData.definition}</p>
        </div>

        <Divider />
      </div>
    </div>
  )
}

const UserIcon = (props: React.ComponentProps<"svg">) => {
  return (
    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M6.90771 11.8932C7.31049 12.296 7.80355 12.4974 8.38688 12.4974C8.97021 12.4974 9.46327 12.296 9.86605 11.8932L13.1994 8.5599C13.6022 8.15712 13.8035 7.66406 13.8035 7.08073C13.8035 6.4974 13.6022 6.00434 13.1994 5.60156C12.7966 5.19878 12.3035 4.9974 11.7202 4.9974C11.1369 4.9974 10.6438 5.19878 10.241 5.60156C9.72716 5.42101 9.22021 5.37934 8.72021 5.47656C8.22021 5.57378 7.7966 5.79601 7.44938 6.14323C7.10216 6.49045 6.87994 6.91406 6.78272 7.41406C6.68549 7.91406 6.72716 8.42101 6.90771 8.9349C6.50494 9.33767 6.30355 9.83073 6.30355 10.4141C6.30355 10.9974 6.50494 11.4905 6.90771 11.8932ZM5.47021 18.3307V14.7474C4.67855 14.0252 4.06396 13.1814 3.62646 12.2161C3.18896 11.2509 2.97021 10.2335 2.97021 9.16406C2.97021 7.08073 3.69938 5.3099 5.15771 3.85156C6.61605 2.39323 8.38688 1.66406 10.4702 1.66406C12.2063 1.66406 13.7445 2.17448 15.0848 3.19531C16.4251 4.21615 17.2966 5.54601 17.6994 7.1849L18.7827 11.4557C18.8522 11.7196 18.8035 11.9592 18.6369 12.1745C18.4702 12.3898 18.248 12.4974 17.9702 12.4974H16.3035V14.9974C16.3035 15.4557 16.1404 15.8481 15.814 16.1745C15.4876 16.5009 15.0952 16.6641 14.6369 16.6641H12.9702V18.3307H11.3035V14.9974H14.6369V10.8307H16.8869L16.0952 7.60156C15.7758 6.33767 15.0952 5.3099 14.0535 4.51823C13.0119 3.72656 11.8174 3.33073 10.4702 3.33073C8.8591 3.33073 7.4841 3.89323 6.34521 5.01823C5.20633 6.14323 4.63688 7.51128 4.63688 9.1224C4.63688 9.95573 4.80702 10.7474 5.1473 11.4974C5.48758 12.2474 5.97021 12.9141 6.59521 13.4974L7.13688 13.9974V18.3307H5.47021Z" fill="currentColor"/>
    </svg>
  )
}
